<template>
  <div class="regulatory-update-add-new-address">
    <HeadingDisplay
      title="Informe seu novo endereço"
      head="Alterar endereço"
      subtitle="O endereço precisa ser no Brasil"
    />

    <div class="regulatory-update-add-new-address__form">
      <InputText
        v-model="address.zipcode"
        label="CEP"
        mask="#####-###"
        :helper-text="getErrorMessage('zipcode')"
        :status="getInputStatus('zipcode')"
        :maxlength="9"
        @focus="removeError('zipcode')"
        @input="debounceInputZipcode"
      />

      <InputText
        v-model="address.street"
        label="Endereço"
        :helper-text="getErrorMessage('street')"
        :status="getInputStatus('street')"
        :disabled="disabled['street']"
        @focus="removeError('street')"
      />

      <div class="regulatory-update-add-new-address__line">
        <InputText
          v-model="address.number"
          class="regulatory-update-add-new-address__line-item"
          label="Número"
          :helper-text="getErrorMessage('number')"
          :status="getInputStatus('number')"
          @focus="removeError('number')"
        />

        <InputText
          v-model="address.complement"
          class="regulatory-update-add-new-address__line-item"
          label="Complemento (opcional)"
          :helper-text="getErrorMessage('complement')"
          :status="getInputStatus('complement')"
          @focus="removeError('complement')"
        />
      </div>

      <div class="regulatory-update-add-new-address__line">
        <InputText
          v-model="address.neighborhood"
          class="regulatory-update-add-new-address__line-item"
          label="Bairro"
          :helper-text="getErrorMessage('neighborhood')"
          :status="getInputStatus('neighborhood')"
          :disabled="disabled['neighborhood']"
          @focus="removeError('neighborhood')"
        />

        <InputSelect
          v-model="address.state"
          class="regulatory-update-add-new-address__line-item"
          label="Estado"
          required
          :options="statesList"
          :helper-text="getErrorMessage('state')"
          :status-type="getInputStatus('state')"
          :disabled="disabled['state']"
          @change="removeError('state')"
        />
      </div>

      <InputText
        v-model="address.city"
        label="Cidade"
        :helper-text="getErrorMessage('city')"
        :status="getInputStatus('city')"
        :disabled="disabled['city']"
        @focus="removeError('city')"
      />
    </div>

    <Message>
      <template #description>
        Declaro para fins de comprovação de residência, que resido no endereço
        acima, e responsabilizo-me civil, administrativa e criminalmente pela
        veracidade das informações declaradas.
      </template>
    </Message>

    <ButtonContainer class="regulatory-update-add-new-address__footer">
      <template #button-primary>
        <ButtonPrimary
          size="large"
          label="Mudar endereço"
          :loading="isButtonLoading"
          @click="handleButtonClick"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { debounce } from 'lodash-es';
import {
  HeadingDisplay,
  MessageFoundation as Message,
  ButtonContainer,
  ButtonPrimary,
  InputText,
  InputSelect
} from '@warrenbrasil/nebraska-web';
import { State, Address } from '@/modules/account/services/resources/types';
import { YupValidationErrors } from '@/modules/common/helpers/yup-validation';
import yupValidation from './validate';
import { getAddressByZipcode } from '@/modules/account/services/resources';
import { InputStatusType } from '../../types';

@Component({
  components: {
    HeadingDisplay,
    Message,
    ButtonContainer,
    ButtonPrimary,
    InputText,
    InputSelect
  }
})
export default class RegulatoryUpdateAddNewAddress extends Vue {
  @Prop({ type: Boolean, default: false })
  public isButtonLoading!: boolean;

  @Prop({ type: Array, required: true })
  public statesList!: State[];

  @Prop({ type: Object, required: true })
  public errors!: YupValidationErrors;

  @Emit()
  public removeError(_key: string) {}

  @Emit()
  public setErrors(_errors: YupValidationErrors) {}

  @Emit()
  private saveAddressAndGoToNextPage(_newAddress: Address) {}

  public address: Address = {
    zipcode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    state: '',
    city: ''
  };

  public disabled: Record<string, boolean> = {};

  public debounceInputZipcode = debounce(this.inputZipcode, 300);

  public getInputStatus(key: string) {
    return this.errors[key] ? InputStatusType.Error : InputStatusType.Default;
  }

  public getErrorMessage(key: string) {
    return this.errors[key] ? this.errors[key][0] : '';
  }

  private setField(field: keyof Address, value?: string) {
    this.address[field] = value || '';
    this.disabled[field] = !!value && !!value.length;
  }

  private setAddressFromZipcode(address: Partial<Address>) {
    this.setField('street', address.street);
    this.setField('neighborhood', address.neighborhood);
    this.setField('state', address.state);
    this.setField('city', address.city);
    this.setField('number', '');
    this.setField('complement', '');
  }

  private async fetchAddressByZipcode(zipcode: string) {
    try {
      const response = await getAddressByZipcode(zipcode);
      if (!response.zipcode) {
        this.setAddressFromZipcode({});
        return;
      }

      this.setAddressFromZipcode(response);
    } catch (_e) {
      this.setAddressFromZipcode({});
    }
  }

  private inputZipcode(zipcode: string) {
    if (zipcode.length === 9) {
      this.fetchAddressByZipcode(zipcode);
    }
  }

  public async handleButtonClick() {
    await this.validateErrors();

    if (yupValidation.isValid()) {
      this.saveAddressAndGoToNextPage(this.address);
    }
  }

  private async validateErrors() {
    try {
      await yupValidation.validate(this.address);
    } catch (_e) {}

    this.setErrors(yupValidation.getAllErrors());
  }
}
</script>

<style lang="less" scoped>
.regulatory-update-add-new-address {
  padding-bottom: @size-spacing-x1800;

  &__form {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x500;

    margin-top: @size-spacing-x800;
    margin-bottom: @size-spacing-x600;
  }

  &__line {
    display: flex;
    gap: @size-spacing-x600;

    &-item {
      width: 50%;
      @media @mobileDown {
        width: 100%;
      }
    }

    @media @mobileDown {
      flex-direction: column;
      gap: @size-spacing-x500;
    }
  }

  &__footer {
    position: fixed;
    left: 0;
    bottom: 0;
    background-color: @background-secondary;
    z-index: 1;
  }
}
</style>
